import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PromptVersionHistory from './PromptVersionHistory';
import PromptLockStatus from './PromptLockStatus';

interface Prompt {
  name: string;
  display_name: string;
  description: string;
  category: string;
  text: string;
  variables: string[];
  variable_warning: string;
}

interface PromptsData {
  system_type: string;
  system_name: string;
  prompts: Prompt[];
  source: string;
}

interface SavedPrompt {
  id: number;
  project_id: number | null;
  system_type: string;
  prompt_name: string;
  prompt_text: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface CategoryConfig {
  order: string[];
  names: Record<string, string>;
  icons: Record<string, string>;
}

interface PromptsEditorProps {
  systemType: string;
  title: string;
  subtitle?: string;
  categories: CategoryConfig;
  onClose?: () => void;
}

const getUserEmail = () => localStorage.getItem('user_email') || 'anonymous@user.com';
const getUserName = () => localStorage.getItem('user_name') || undefined;

export default function PromptsEditor({ systemType, title, subtitle, categories, onClose }: PromptsEditorProps) {
  const { isAdmin } = useAuth();
  const [prompts, setPrompts] = useState<PromptsData | null>(null);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [editedText, setEditedText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Version control state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [canEdit, setCanEdit] = useState(true);
  const userEmail = getUserEmail();
  const userName = getUserName();

  const canEditPrompts = isAdmin && canEdit;

  useEffect(() => {
    loadPrompts();
  }, [systemType]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load default prompts from file system
      const defaultsResponse = await api.get(`/prompts/defaults/${systemType}`);
      setPrompts(defaultsResponse.data);

      // Load saved global prompts (project_id=null)
      const savedResponse = await api.get(`/prompts?system_type=${systemType}`);
      // Filter to only global (no project_id) prompts
      const globalPrompts = (savedResponse.data || []).filter((p: SavedPrompt) => p.project_id === null);
      setSavedPrompts(globalPrompts);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentText = (promptName: string): string => {
    // Check for saved global override first
    const saved = savedPrompts.find(p => p.prompt_name === promptName);
    if (saved) return saved.prompt_text;
    // Fall back to default
    const defaultPrompt = prompts?.prompts.find(p => p.name === promptName);
    return defaultPrompt?.text || '';
  };

  const handleSelectPrompt = (promptName: string) => {
    if (hasChanges && selectedPrompt) {
      if (!confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedPrompt(promptName);
    setEditedText(getCurrentText(promptName));
    setHasChanges(false);
    setSuccessMessage(null);
  };

  const handleTextChange = (newText: string) => {
    setEditedText(newText);
    setHasChanges(newText !== getCurrentText(selectedPrompt || ''));
  };

  const handleSave = async () => {
    if (!selectedPrompt) return;

    try {
      setSaving(true);
      setError(null);

      const existingPrompt = savedPrompts.find(p => p.prompt_name === selectedPrompt);

      if (existingPrompt) {
        // Update existing with versioning
        await api.put(`/prompts/${existingPrompt.id}/versioned`, {
          prompt_text: editedText,
          change_summary: changeSummary || undefined,
          editor_email: userEmail,
          editor_name: userName
        });
      } else {
        // Create new global prompt (no project_id)
        await api.post('/prompts', {
          system_type: systemType,
          prompt_name: selectedPrompt,
          prompt_text: editedText,
          is_default: false
        });
      }

      // Reload saved prompts
      const savedResponse = await api.get(`/prompts?system_type=${systemType}`);
      const globalPrompts = (savedResponse.data || []).filter((p: SavedPrompt) => p.project_id === null);
      setSavedPrompts(globalPrompts);

      setHasChanges(false);
      setChangeSummary('');
      setSuccessMessage('Prompt saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      if (err.response?.status === 423) {
        setError('Prompt is locked by another user. Cannot save.');
      } else {
        setError(err.response?.data?.detail || 'Failed to save prompt');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedPrompt) return;

    if (!confirm('Reset this prompt to the global default? This will delete your customization.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const existingPrompt = savedPrompts.find(p => p.prompt_name === selectedPrompt);
      if (existingPrompt) {
        await api.delete(`/prompts/${existingPrompt.id}`);
      }

      // Reload saved prompts
      const savedResponse = await api.get(`/prompts?system_type=${systemType}`);
      const globalPrompts = (savedResponse.data || []).filter((p: SavedPrompt) => p.project_id === null);
      setSavedPrompts(globalPrompts);

      // Reset to default text
      const defaultPrompt = prompts?.prompts.find(p => p.name === selectedPrompt);
      setEditedText(defaultPrompt?.text || '');
      setHasChanges(false);
      setSuccessMessage('Prompt reset to global default!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset prompt');
    } finally {
      setSaving(false);
    }
  };

  const getVariables = (promptName: string): string[] => {
    const prompt = prompts?.prompts.find(p => p.name === promptName);
    return prompt?.variables || [];
  };

  const isCustomized = (promptName: string): boolean => {
    return savedPrompts.some(p => p.prompt_name === promptName);
  };

  const getSelectedPromptData = (): Prompt | null => {
    if (!selectedPrompt) return null;
    return prompts?.prompts.find(p => p.name === selectedPrompt) || null;
  };

  const selectedPromptData = getSelectedPromptData();

  const getSelectedPromptId = (): number | null => {
    if (!selectedPrompt) return null;
    const saved = savedPrompts.find(p => p.prompt_name === selectedPrompt);
    return saved?.id || null;
  };

  const handleVersionRestore = (restoredText: string) => {
    setEditedText(restoredText);
    setHasChanges(true);
    setShowVersionHistory(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="spinner mr-2"></span>
        <span className="text-gray-500">Loading prompts...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">
              {subtitle}
              {prompts?.source && <span className="ml-1">(Source: {prompts.source})</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {onClose && (
            <button onClick={onClose} className="btn-ghost">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-danger-700">{error}</span>
        </div>
      )}

      {/* Success Alert */}
      {successMessage && (
        <div className="p-4 bg-success-50 border border-success-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-success-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-success-700">{successMessage}</span>
        </div>
      )}

      {/* Non-Admin Warning */}
      {!isAdmin && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-blue-800 font-medium">View Only</p>
              <p className="text-sm text-blue-700 mt-1">
                Editing requires admin privileges.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Prompt List - Grouped by Category */}
        <div className="lg:col-span-1 space-y-4">
          {categories.order.map(category => {
            const categoryPrompts = prompts?.prompts.filter(p => p.category === category) || [];
            if (categoryPrompts.length === 0) return null;

            return (
              <div key={category} className="card">
                <div className="card-header bg-gray-50 py-2 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{categories.icons[category] || '📝'}</span>
                    <h3 className="font-semibold text-gray-700 text-sm">
                      {categories.names[category] || category}
                    </h3>
                    <span className="text-xs text-gray-400">({categoryPrompts.length})</span>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {categoryPrompts.map((prompt) => (
                    <button
                      key={prompt.name}
                      onClick={() => handleSelectPrompt(prompt.name)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        selectedPrompt === prompt.name ? 'bg-brand-50 border-l-4 border-brand-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-900 text-sm block truncate">
                            {prompt.display_name || prompt.name}
                          </span>
                          {prompt.description && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">
                              {prompt.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {isCustomized(prompt.name) ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              Custom
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              Global
                            </span>
                          )}
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Prompt Editor */}
        <div className="lg:col-span-2">
          {selectedPrompt && selectedPromptData ? (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{categories.icons[selectedPromptData.category] || '📝'}</span>
                      <h3 className="font-semibold text-gray-900">
                        {selectedPromptData.display_name || selectedPrompt}
                      </h3>
                      {isCustomized(selectedPrompt) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          Custom
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          Global
                        </span>
                      )}
                      {hasChanges && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-800">
                          Unsaved Changes
                        </span>
                      )}
                    </div>
                    {selectedPromptData.description && (
                      <p className="text-sm text-gray-500 mt-1">{selectedPromptData.description}</p>
                    )}
                  </div>
                  {/* Version History Button */}
                  <button
                    onClick={() => setShowVersionHistory(!showVersionHistory)}
                    className={`btn-ghost text-sm ${!getSelectedPromptId() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={getSelectedPromptId() ? 'View version history' : 'No change history yet (prompt is using global default)'}
                    disabled={!getSelectedPromptId()}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    History
                  </button>
                </div>

                {/* Lock Status */}
                {getSelectedPromptId() && isAdmin && (
                  <div className="mt-3">
                    <PromptLockStatus
                      promptId={getSelectedPromptId()}
                      userEmail={userEmail}
                      userName={userName}
                      onLockStatusChange={setCanEdit}
                    />
                  </div>
                )}
              </div>

              {/* Version History Panel */}
              {showVersionHistory && getSelectedPromptId() && (
                <div className="border-b border-gray-200 p-4 bg-gray-50">
                  <PromptVersionHistory
                    promptId={getSelectedPromptId()!}
                    promptName={selectedPromptData.display_name || selectedPrompt}
                    currentText={editedText}
                    userEmail={userEmail}
                    userName={userName}
                    onRestore={handleVersionRestore}
                    onClose={() => setShowVersionHistory(false)}
                  />
                </div>
              )}

              <div className="card-body space-y-4">
                {/* Variables Warning */}
                {getVariables(selectedPrompt).length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-amber-800">Required Variables</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Do NOT remove these placeholders:
                          <code className="ml-1 px-1 bg-amber-100 rounded">
                            {getVariables(selectedPrompt).map(v => `{${v}}`).join(', ')}
                          </code>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Editor */}
                <div>
                  <textarea
                    value={editedText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    disabled={saving || !canEditPrompts}
                    className={`textarea font-mono text-sm h-96 ${!canEditPrompts ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Select a prompt to edit..."
                  />
                </div>

                {/* Character/Word Count */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{editedText.length.toLocaleString()} characters</span>
                  <span>{editedText.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
                </div>

                {/* Change Summary (for versioning) */}
                {hasChanges && isCustomized(selectedPrompt) && isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Change Summary (optional)
                    </label>
                    <input
                      type="text"
                      value={changeSummary}
                      onChange={(e) => setChangeSummary(e.target.value)}
                      placeholder="Brief description of your changes..."
                      className="input text-sm"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will be recorded in the version history
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {isAdmin && (
                <div className="card-footer flex items-center justify-between">
                  <button
                    onClick={handleReset}
                    disabled={saving || !isCustomized(selectedPrompt) || !canEditPrompts}
                    className="btn-secondary"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset to Global
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges || !canEditPrompts}
                    className="btn-save"
                    title={!canEditPrompts ? 'Prompt is locked by another user' : ''}
                  >
                    {saving ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="card-body flex flex-col items-center justify-center py-16 text-gray-500">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="font-medium">Select a Prompt</p>
                <p className="text-sm mt-1">Choose a prompt template from the list to view or edit</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
